// GraphQL operations for the per-project generation profiles: Voice_Profiles
// (Perfiles de voz) and Art_Style_Profiles (Estilos visuales).
//
// Each frontend feature owns its own operations file. This one backs the two
// profile tabs (task 16.6): a query to list a project's existing profiles and
// the two create mutations. To list profiles we query the `voiceProfiles` and
// `artStyleProfiles` fields the schema exposes on `Project` (see the design's
// GraphQL Schema / getProject), scoped to the project the tab is showing —
// without touching graphql/project-detail.ts, which keeps its own lean
// getProject document for the header/members surface.
//
// Requirements 10.1, 10.3, 10.5 (define up to 20 profiles per type, single
// default per type). Design → GraphQL Schema
// (Query.getProject.voiceProfiles/artStyleProfiles,
// Mutation.createVoiceProfile/createArtStyleProfile) and Frontend (Voice/Art
// profile screens).

import { gql } from '@apollo/client';
import type { ArtStyleProfile, AwsJson, VoiceProfile } from '@courseai/domain';

/**
 * The Voice_Profile fields the Perfiles de voz tab renders and the create
 * mutation returns: identity, the human name, the ElevenLabs voice id, the
 * content language, and whether it is the project default. `voiceSettings` and
 * `pausePattern` are free-form AWSJSON; they are fetched so a created profile
 * hydrates the cache completely.
 */
export type VoiceProfileFields = Pick<
  VoiceProfile,
  | 'projectId'
  | 'voiceProfileId'
  | 'name'
  | 'voiceId'
  | 'language'
  | 'voiceSettings'
  | 'pausePattern'
  | 'isDefault'
  | 'createdAt'
>;

/**
 * The Art_Style_Profile fields the Estilos visuales tab renders and the create
 * mutation returns.
 */
export type ArtStyleProfileFields = Pick<
  ArtStyleProfile,
  | 'projectId'
  | 'artStyleProfileId'
  | 'name'
  | 'artStyle'
  | 'colorPalette'
  | 'veoPromptHints'
  | 'negativePrompt'
  | 'aspectRatio'
  | 'resolution'
  | 'referenceImageKeys'
  | 'isDefault'
  | 'createdAt'
>;

/** Variables for the `getProject`-scoped profiles query. */
export interface ProjectProfilesVariables {
  readonly projectId: string;
}

/**
 * Shape of the profiles query result. `getProject` is nullable server-side (a
 * non-member receives an authorization error, and the field resolves to null);
 * the tabs treat an absent project as an empty list.
 */
export interface ProjectProfilesData {
  readonly getProject: {
    readonly projectId: string;
    readonly voiceProfiles: ReadonlyArray<VoiceProfileFields>;
    readonly artStyleProfiles: ReadonlyArray<ArtStyleProfileFields>;
  } | null;
}

/**
 * Load a project's Voice_Profiles and Art_Style_Profiles for the profile tabs.
 * Membership is enforced server-side; identity is derived from the JWT, so no
 * identity is sent. This is a distinct document from the project-detail header
 * query so the two tabs can cache and refetch profiles independently.
 */
export const GET_PROJECT_PROFILES = gql`
  query GetProjectProfiles($projectId: ID!) {
    getProject(projectId: $projectId) {
      projectId
      voiceProfiles {
        projectId
        voiceProfileId
        name
        voiceId
        language
        voiceSettings
        pausePattern
        isDefault
        createdAt
      }
      artStyleProfiles {
        projectId
        artStyleProfileId
        name
        artStyle
        colorPalette
        veoPromptHints
        negativePrompt
        aspectRatio
        resolution
        referenceImageKeys
        isDefault
        createdAt
      }
    }
  }
`;

/** Variables for `createVoiceProfile`. Mirrors `CreateVoiceProfileInput`. */
export interface CreateVoiceProfileVariables {
  readonly input: {
    readonly projectId: string;
    readonly name: string;
    readonly voiceId: string;
    readonly language: string;
    /** Free-form AWSJSON serialized as a JSON string on the wire. */
    readonly voiceSettings?: AwsJson | undefined;
    readonly pausePattern?: AwsJson | undefined;
    readonly isDefault?: boolean | undefined;
  };
}

/** Shape of the `createVoiceProfile` mutation result. */
export interface CreateVoiceProfileData {
  readonly createVoiceProfile: VoiceProfileFields;
}

/**
 * Create a Voice_Profile scoped to the project. Owner/editor only, enforced
 * server-side (Req 10.2). The server rejects a create once the project already
 * holds 20 Voice_Profiles (Req 10.1) and, when `isDefault` is set, clears the
 * previous default of this type in one transaction (Req 10.5). The client
 * mirrors the count limit for immediate feedback.
 */
export const CREATE_VOICE_PROFILE = gql`
  mutation CreateVoiceProfile($input: CreateVoiceProfileInput!) {
    createVoiceProfile(input: $input) {
      projectId
      voiceProfileId
      name
      voiceId
      language
      voiceSettings
      pausePattern
      isDefault
      createdAt
    }
  }
`;

/** Variables for `createArtStyleProfile`. Mirrors `CreateArtStyleProfileInput`. */
export interface CreateArtStyleProfileVariables {
  readonly input: {
    readonly projectId: string;
    readonly name: string;
    readonly artStyle: string;
    readonly colorPalette: readonly string[];
    readonly veoPromptHints?: string | undefined;
    readonly negativePrompt?: string | undefined;
    readonly aspectRatio?: string | undefined;
    readonly resolution?: string | undefined;
    readonly referenceImageKeys?: readonly string[] | undefined;
    readonly isDefault?: boolean | undefined;
  };
}

/** Shape of the `createArtStyleProfile` mutation result. */
export interface CreateArtStyleProfileData {
  readonly createArtStyleProfile: ArtStyleProfileFields;
}

/**
 * Create an Art_Style_Profile scoped to the project. Owner/editor only,
 * enforced server-side (Req 10.2). The server rejects a create once the project
 * already holds 20 Art_Style_Profiles (Req 10.3) and, when `isDefault` is set,
 * clears the previous default of this type in one transaction (Req 10.5).
 */
export const CREATE_ART_STYLE_PROFILE = gql`
  mutation CreateArtStyleProfile($input: CreateArtStyleProfileInput!) {
    createArtStyleProfile(input: $input) {
      projectId
      artStyleProfileId
      name
      artStyle
      colorPalette
      veoPromptHints
      negativePrompt
      aspectRatio
      resolution
      referenceImageKeys
      isDefault
      createdAt
    }
  }
`;

/** Variables for `getMediaUploadUrl` (presigned PUT for reference images). */
export interface GetMediaUploadUrlVariables {
  readonly input: {
    readonly projectId: string;
    readonly fileName: string;
    readonly contentType: string;
  };
}

/** Result of `getMediaUploadUrl`: the presigned PUT URL and the key to store. */
export interface GetMediaUploadUrlData {
  readonly getMediaUploadUrl: {
    readonly url: string;
    readonly objectKey: string;
    readonly expiresAt: string;
  };
}

/**
 * Request a short-lived presigned S3 PUT URL to upload a reference image. The
 * client PUTs the file bytes to `url` (with the same Content-Type), then stores
 * the returned `objectKey` on the Art_Style_Profile's referenceImageKeys.
 */
export const GET_MEDIA_UPLOAD_URL = gql`
  mutation GetMediaUploadUrl($input: GetMediaUploadUrlInput!) {
    getMediaUploadUrl(input: $input) {
      url
      objectKey
      expiresAt
    }
  }
`;
